import { useEffect, useState, useRef, useCallback, useLayoutEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import chatService from '../../services/chat.service';
import config from '../../config';
import { useSelector } from 'react-redux';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import DotSpinner from '../../components/shared/DotSpinner';
import { formatTime } from '../../utils/formatTime';
import { CheckIsBlocked } from './CheckIsBlocked';
import { useObserver } from '../../hooks/useObserver';
import { useChat } from '../../context/ChatProvider';
import { toast } from 'react-toastify';
import { Button } from '../../components/Button';

function mergeMessages(currentMessages = [], incomingMessages = []) {
  const messagesMap = new Map();

  [...currentMessages, ...incomingMessages].forEach((message) => {
    if (!message?._id) return;

    const messageId = String(message._id);
    const previousMessage = messagesMap.get(messageId) || {};
    messagesMap.set(messageId, { ...previousMessage, ...message });
  });

  return Array.from(messagesMap.values()).sort(
    (left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()
  );
}

function shouldRedirectToChatList(error) {
  const status = error?.response?.status;
  const message = error?.response?.data?.message;

  return status === 404 || message === 'Chat partner not found' || message === 'Chat not found';
}

function ChatView() {
  const navigate = useNavigate();
  const location = useLocation();
  const axiosPrivate = useAxiosPrivate();

  const isDisabledChat = location.state?.disabled || false;

  const { chat_id } = useParams();
  const { userId: currentUserId } = useSelector((state) => state.auth);
  const [partnerId, setPartnerId] = useState(null);
  const { markChatAsRead } = useChat();

  const [otherParticipant, setOtherParticipant] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isFirstMessage, setIsFirstMessage] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [messageInput, setMessageInput] = useState('');

  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);

  const messagesContainerRef = useRef(null);
  const loadedPagesRef = useRef(new Set());
  const pageRef = useRef(1);
  const totalItemsRef = useRef(null);
  const messagesCountRef = useRef(0);

  const [totalItems, setTotalItems] = useState(null);
  const [page, setPage] = useState(1);

  const showFirstMessagePrompt = isFirstMessage && messages.length === 0;

  const isLoadingMessageRef = useRef(false);

  const latestMessageRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const messageInputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    const container = messagesContainerRef.current;

    if (!container) return;

    container.scrollTop = container.scrollHeight;
  }, []);

  useLayoutEffect(() => {
    const latestMessage = messages[messages.length - 1];

    if (!latestMessage) {
      latestMessageRef.current = null;
      return;
    }

    const latestCreatedAt = new Date(latestMessage.createdAt).getTime();

    if (latestMessageRef.current !== null && latestCreatedAt > latestMessageRef.current) {
      scrollToBottom();

      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }

    latestMessageRef.current = latestCreatedAt;
  }, [messages, scrollToBottom]);

  useLayoutEffect(() => {
    if (!isInitialLoadRef.current) return;
    if (!messages.length) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    isInitialLoadRef.current = false;

    container.scrollTop = container.scrollHeight;

    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;

      setTimeout(() => {
        container.scrollTop = container.scrollHeight;
      }, 50);
    });
  }, [messages]);

  const fetchChatData = useCallback(
    async (nextPage, { force = false } = {}) => {
      if (!chat_id || isLoadingMessageRef.current) return;
      if (!force && loadedPagesRef.current.has(nextPage)) return;

      isLoadingMessageRef.current = true;
      loadedPagesRef.current.add(nextPage);

      const prevHeight = messagesContainerRef.current?.scrollHeight ?? 0;

      try {
        const chatData = await chatService.getChatHistory(axiosPrivate, chat_id, nextPage);
        const history = chatData.data.chats;

        if (!history?.partner?._id) {
          navigate(config.ROUTES.CHATS.LIST, { replace: true });
          return;
        }

        setPartnerId(history.partner._id);

        setOtherParticipant(history.partner);
        setMessages((prevMessages) => mergeMessages(prevMessages, history.items));
        setIsFirstMessage(history.isFirstMessage && history.items.length === 0);

        setTotalItems(history.total);
        totalItemsRef.current = history.total;
        setPage(nextPage);
        pageRef.current = nextPage;

        requestAnimationFrame(() => {
          const container = messagesContainerRef.current;
          if (!container) return;

          if (nextPage === 1) {
            container.scrollTop = container.scrollHeight;
            return;
          }

          const newHeight = container.scrollHeight;
          container.scrollTop += newHeight - prevHeight;
        });
      } catch (error) {
        loadedPagesRef.current.delete(nextPage);

        if (shouldRedirectToChatList(error)) {
          navigate(config.ROUTES.CHATS.LIST, { replace: true });
          toast.error('Чат не найден. Возможно, он был удалён.');
          return;
        }

        console.error('Error fetching chat data:', error);
      } finally {
        isLoadingMessageRef.current = false;
      }
    },
    [axiosPrivate, chat_id, navigate]
  );

  const targetRef = useObserver({
    root: messagesContainerRef.current,
    onIntersecting: () => {
      if (isLoadingChat) return;

      if (messagesCountRef.current === 0) return;

      if (totalItemsRef.current !== null && messagesCountRef.current >= totalItemsRef.current) {
        return;
      }

      fetchChatData(pageRef.current + 1);
    },
    rootMargin: '100px 0px 0px 0px'
  });

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    totalItemsRef.current = totalItems;
  }, [totalItems]);

  useEffect(() => {
    messagesCountRef.current = messages.length;
  }, [messages.length]);

  const syncChatReadState = useCallback(() => {
    if (!currentUserId || !chat_id) return;

    chatService.markAsRead(chat_id, currentUserId);
    markChatAsRead(chat_id);
  }, [chat_id, currentUserId, markChatAsRead]);

  useEffect(() => {
    if (!currentUserId || !chat_id) {
      navigate('/');
      return;
    }

    chatService.joinChat(chat_id);
    syncChatReadState();

    const unsubscribeMessage = chatService.onMessage((newMessages) => {
      if (String(newMessages.chatId) !== String(chat_id)) {
        return;
      }
      console.log(newMessages);

      const items = newMessages.data.items || [];

      setMessages((prevMessages) => mergeMessages(prevMessages, items));

      if (items.length > 0) {
        setIsFirstMessage(false);
      }

      const hasUnreadIncoming = items.some(
        (message) => String(message.from) !== String(currentUserId) && !message.readAt
      );

      if (hasUnreadIncoming) {
        syncChatReadState();
      }
    });

    const unsubscribeMessageRead = chatService.onMessageRead((data) => {
      if (String(data.chatId) !== String(chat_id)) {
        return;
      }

      if (data.ids.length) {
        setMessages((prevMessages) =>
          prevMessages.map((msg) => (data.ids.includes(msg._id) ? { ...msg, readAt: new Date().toISOString() } : msg))
        );
      }
    });

    const unsubscribeTyping = chatService.onTyping((data) => {
      if (String(data.chatId) !== String(chat_id)) {
        return;
      }
      setIsTyping(data.isTyping);
    });

    return () => {
      chatService.leaveChat(chat_id);
      unsubscribeMessage();
      unsubscribeTyping();
      unsubscribeMessageRead();
    };
  }, [chat_id, currentUserId, navigate, scrollToBottom, syncChatReadState]);

  useEffect(() => {
    loadedPagesRef.current.clear();
    pageRef.current = 1;
    totalItemsRef.current = null;
    messagesCountRef.current = 0;
    setOtherParticipant(null);
    setMessages([]);
    setIsFirstMessage(false);
    setTotalItems(null);
    setPage(1);
    setIsLoadingChat(true);
    fetchChatData(1).finally(() => {
      setIsLoadingChat(false);
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    });
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [chat_id, fetchChatData, scrollToBottom]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setMessageInput(value);

    if (!isTypingRef.current) {
      chatService.startTyping(chat_id, currentUserId);
      isTypingRef.current = true;
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (value.trim().length === 1) {
      scrollToBottom();
    }

    typingTimeoutRef.current = setTimeout(() => {
      chatService.stopTyping(chat_id, currentUserId);
      isTypingRef.current = false;
    }, 1500);
  };

  const handleSendMessage = () => {
    const text = messageInput.trim();

    if (!text) return;

    chatService.sendMessage(chat_id, currentUserId, text);

    setMessageInput('');
    chatService.stopTyping(chat_id, currentUserId);

    if (isFirstMessage) {
      setIsFirstMessage(false);
    }

    messageInputRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (isLoadingChat) {
    return (
      <div className="flex items-center justify-center min-h-[100vh]">
        <DotSpinner />
      </div>
    );
  }

  return (
    <CheckIsBlocked userId={otherParticipant?._id}>
      <div className="w-full mb-[0px] h-full flex justify-start items-center flex-col relative">
        <div
          className="flex items-center justify-center text-white text-[24px] w-full px-4 pb-3 pt-[21px]
            border-b border-[#233636]"
        >
          <div className="w-full flex items-center justify-center">
            <div className="w-full flex justify-between items-center relative">
              <div className="flex items-center gap-[10px]">
                <img
                  src="/icons/Button-back.svg"
                  onClick={() => {
                    if (location.state?.from?.startsWith(config.ROUTES.NOTIFICATIONS)) {
                      navigate(config.ROUTES.NOTIFICATIONS);
                      return;
                    }
                    navigate(config.ROUTES.CHATS.LIST);
                  }}
                  className="w-[44px] h-[44px]"
                />
              </div>
              <div
                className={`flex-1 flex items-center gap-2 justify-center min-w-0 ${isDisabledChat ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={() => {
                  if (isDisabledChat) return;
                  navigate(config.ROUTES.CHATS.PROFILE(otherParticipant?._id), {
                    state: {
                      from: `/chats/${chat_id}`
                    }
                  });
                }}
              >
                <img
                  src={otherParticipant?.photo || config.FALLBACK_AVATAR}
                  className="w-[32px] h-[32px] rounded-full shrink-0 object-cover"
                />
                <p className="text-center truncate text-[16px] font-semibold">{otherParticipant?.name || '...'}</p>
              </div>
              <button
                onClick={() => navigate(config.ROUTES.GIFTS.SEND, { state: { recipientId: partnerId } })}
                className="size-[44px] flex items-center justify-center shrink-0"
              >
                <img src="/icons/gifts.svg" alt="chat" />
              </button>
            </div>
          </div>
        </div>
        <div
          ref={messagesContainerRef}
          className="w-full h-full flex-1 flex justify-start items-center flex-col text-white px-4 pt-4 pb-5 font-raleway overflow-y-auto"
        >
          <div ref={targetRef} className="h-2" />
          {messages?.map((message, index) => {
            const isCurrentUser = message.from === currentUserId;
            const isTextType = message.type === 'text';
            const isEventType = message.type === 'event' && message.event;
            return (
              <div
                key={message._id}
                className={`${isCurrentUser ? 'self-end' : 'self-start'} ${
                  isEventType
                    ? 'bg-[#00380F] border border-[#233636]'
                    : isCurrentUser
                      ? 'bg-[#0A2438] border border-[#233636]'
                      : 'bg-[#032A2A] border border-[#233636]'
                } ${
                  isCurrentUser
                    ? 'rounded-tr-[16px] rounded-tl-[16px] rounded-bl-[16px]'
                    : 'rounded-tr-[16px] rounded-tl-[16px] rounded-br-[16px]'
                } 
                            relative flex max-w-[90%] min-w-[80px] mt-[16px] leading-[1.5] p-[10px] pb-[30px]`}
              >
                {isEventType ? (
                  <div
                    onClick={() => navigate(config.ROUTES.EVENTS.DETAIL(message.event._id))}
                    className="flex flex-col gap-2 min-w-[210px]"
                  >
                    <div className="flex items-center gap-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M22.5 13.5H18C16.8065 13.5 15.6619 13.9741 14.818 14.818C13.9741 15.6619 13.5 16.8065 13.5 18V22.5H4.5C3.70435 22.5 2.94129 22.1839 2.37868 21.6213C1.81607 21.0587 1.5 20.2956 1.5 19.5V10.5H22.5V13.5Z"
                          fill="#A1F69E"
                        />
                        <path
                          d="M15 22.5V18C15 17.2044 15.3161 16.4413 15.8787 15.8787C16.4413 15.3161 17.2044 15 18 15H22.5L15 22.5ZM18 1.5C18.3978 1.5 18.7794 1.65804 19.0607 1.93934C19.342 2.22064 19.5 2.60218 19.5 3V4.5C20.2956 4.5 21.0587 4.81607 21.6213 5.37868C22.1839 5.94129 22.5 6.70435 22.5 7.5V9H1.5V7.5C1.5 6.70435 1.81607 5.94129 2.37868 5.37868C2.94129 4.81607 3.70435 4.5 4.5 4.5V3C4.5 2.60218 4.65804 2.22064 4.93934 1.93934C5.22064 1.65804 5.60218 1.5 6 1.5C6.39782 1.5 6.77936 1.65804 7.06066 1.93934C7.34196 2.22064 7.5 2.60218 7.5 3V4.5H16.5V3C16.5 2.60218 16.658 2.22064 16.9393 1.93934C17.2206 1.65804 17.6022 1.5 18 1.5Z"
                          fill="#A1F69E"
                        />
                      </svg>

                      <p className="text-[14px] tracking-wide">Приглашение на мероприятие</p>
                    </div>
                    <p className="text-white text-[16px] font-bold leading-snug line-clamp-2">{message.event.title}</p>
                    <button className="flex items-center gap-1 text-[#A1F69E] text-[16px] font-semibold">
                      Узнать подробнее
                      <svg width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M-7.53403e-05 5.4082H12.3076M7.30762 0.408203L12.3076 5.4082L7.30762 10.4082"
                          stroke="#A1F69E"
                          stroke-width="1.15385"
                        ></path>
                      </svg>
                    </button>
                  </div>
                ) : isTextType ? (
                  <div className={`flex-1 min-w-0 break-words text-left`}>{message.text}</div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <img src={message.gift.imageUrl} className="size-10 mx-auto" />
                    <p className="text-[#A1F69E] text-[13px] max-w-[125px]">{message.gift.name}</p>
                  </div>
                )}
                <span className="absolute flex flex-row bottom-[10px] right-[10px] text-[12px]">
                  {formatTime(message.createdAt)}
                  {isCurrentUser && (
                    <span className="ml-[6px] flex items-center justify-center w-full">
                      {message.readAt ? (
                        <img src="/icons/msg_status_read.png" className="w-[18px] h-[18px]" />
                      ) : (
                        <img src="/icons/msg_status_delivered.png" className="w-[18px] h-[18px]" />
                      )}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
        <div className="font-raleway w-full flex items-center justify-center text-white text-[14px] opacity-80">
          <div className="w-full px-4 flex justify-start">
            {isTyping && (
              <span className="flex flex-row justify-start items-center">
                <img src="/icons/writing_message.png" className="mr-[5px] w-[12px] h-[12px]" />
                {otherParticipant?.name} пишет сообщение
              </span>
            )}
          </div>
        </div>
        <div className={`w-full flex items-center p-4 justify-center text-white font-raleway`}>
          <input
            disabled={isDisabledChat}
            ref={messageInputRef}
            id="msginput"
            placeholder="Сообщение"
            className="text-[18px] pl-4 text-white w-full h-[64px] bg-[#FFFFFF33] rounded-[400px] disabled:opacity-30 disabled:cursor-not-allowed"
            value={messageInput}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            onFocus={() => {
              requestAnimationFrame(() => {
                scrollToBottom();

                setTimeout(() => {
                  scrollToBottom();
                }, 500);
              });
            }}
            maxLength={300}
          />
          <button
            disabled={isDisabledChat}
            className="ml-[10px] cursor-pointer w-[64px] h-[64px] flex items-center justify-center shrink-0 bg-[#A1F69E] rounded-[50%] disabled:opacity-30 disabled:cursor-not-allowed"
            onClick={handleSendMessage}
          >
            <img src="/icons/send_msg.png" className="w-[24px] h-[24px] shrink-0" />
          </button>
        </div>
      </div>
      {showFirstMessagePrompt && !isDisabledChat && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-5 flex flex-col items-center justify-center gap-7 w-full">
          <img src="/icons/heart-caht.svg" alt="hart" className="size-[48px]" />
          <div className="inline-flex">
            <p className="text-[20px] font-medium text-white text-center">
              У вас взаимная симпатия! <br /> Начните диалог
            </p>
          </div>
        </div>
      )}
      {isDisabledChat && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-5 flex flex-col items-center justify-center gap-7 w-full">
          <img src="/icons/f7_gift-fill.svg" alt="hart" className="size-[70px]" />
          <div className="inline-flex">
            <p className="text-[20px] font-medium text-white text-center">
              Сделайте первый шаг и напишите сообщение за подарок
            </p>
          </div>
          <Button
            className="w-max"
            onClick={() => navigate(config.ROUTES.GIFTS.SEND, { state: { recipientId: partnerId } })}
          >
            <img src="/icons/f7_gift-fill-smoll.svg" alt="hart" className="size-[24px]" />
            Выбрать подарок
          </Button>
        </div>
      )}
    </CheckIsBlocked>
  );
}

export default ChatView;
